/**
 * Notification Sound Service
 * Plays sounds for job alerts and notifications
 */

class NotificationSoundService {
  constructor() {
    this.audioContext = null;
    this.enabled = localStorage.getItem('notification_sound_enabled') !== 'false';
    this.volume = parseFloat(localStorage.getItem('notification_volume') || '0.5');
  }

  // Initialize audio context (must be called after user interaction)
  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this;
  }

  // Enable/disable sounds
  setEnabled(enabled) {
    this.enabled = enabled;
    localStorage.setItem('notification_sound_enabled', enabled.toString());
  }

  // Set volume (0-1)
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    localStorage.setItem('notification_volume', this.volume.toString());
  }

  // Play a tone with given frequency and duration
  playTone(frequency, duration = 200, type = 'sine') {
    if (!this.enabled || !this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = type;

      gainNode.gain.setValueAtTime(this.volume, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + duration / 1000);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + duration / 1000);
    } catch (e) {
      console.debug('Sound playback failed:', e);
    }
  }

  // Play success chime (ascending tones)
  playSuccess() {
    if (!this.audioContext) this.init();
    this.playTone(523.25, 150); // C5
    setTimeout(() => this.playTone(659.25, 150), 150); // E5
    setTimeout(() => this.playTone(783.99, 200), 300); // G5
  }

  // Play new job alert (attention-grabbing)
  playNewJobAlert() {
    if (!this.audioContext) this.init();
    // Play a more attention-grabbing sequence
    this.playTone(880, 100); // A5
    setTimeout(() => this.playTone(880, 100), 150);
    setTimeout(() => this.playTone(1046.5, 200), 300); // C6
  }

  // Play urgent alert (rapid beeps)
  playUrgentAlert() {
    if (!this.audioContext) this.init();
    for (let i = 0; i < 3; i++) {
      setTimeout(() => this.playTone(1000, 80), i * 120);
    }
  }

  // Play notification sound
  playNotification() {
    if (!this.audioContext) this.init();
    this.playTone(660, 100);
    setTimeout(() => this.playTone(880, 150), 100);
  }

  // Play error sound
  playError() {
    if (!this.audioContext) this.init();
    this.playTone(200, 300, 'sawtooth');
  }

  // Play click/tap sound
  playClick() {
    if (!this.audioContext) this.init();
    this.playTone(1200, 30, 'square');
  }
}

// Singleton instance
const notificationSound = new NotificationSoundService();

export default notificationSound;

// Named exports for convenience
export const playNewJobAlert = () => notificationSound.playNewJobAlert();
export const playSuccess = () => notificationSound.playSuccess();
export const playNotification = () => notificationSound.playNotification();
export const playUrgentAlert = () => notificationSound.playUrgentAlert();
export const playError = () => notificationSound.playError();
export const playClick = () => notificationSound.playClick();
export const initSound = () => notificationSound.init();
export const setSoundEnabled = (enabled) => notificationSound.setEnabled(enabled);
export const setSoundVolume = (volume) => notificationSound.setVolume(volume);
